import { Box, Button, Typography } from "@mui/material"
import { Send, TestTube } from "lucide-react"
import { BackButton } from "@/components/ui/back-button"

interface FormHeaderProps {
  isEditMode: boolean
  isScrolled: boolean
  isSaving: boolean
  onBackClick: () => void
  onSave: () => void
  // Hide the actions until there's something to act on (a template chosen).
  canSave?: boolean
  // Send a test email — sits next to Save in the top bar.
  onSendTest?: () => void
}

export default function FormHeader({
  isEditMode,
  isScrolled,
  isSaving,
  onBackClick,
  onSave,
  canSave = true,
  onSendTest,
}: FormHeaderProps) {
  return (
    <Box
      sx={{
        position: "sticky",
        // Match the AppShell top header EXACTLY (56px / 3.5rem). At top:64 an 8px
        // gap opened above this bar and page content scrolled through it.
        top: "var(--app-header-height, 56px)",
        zIndex: 40,
        py: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        transition: "all 0.2s",
        ...(isScrolled
          ? {
              bgcolor: "background.paper",
              borderBottom: 1,
              borderColor: "divider",
              boxShadow: 3,
              mx: { xs: -2, sm: -3, lg: -4 },
              px: { xs: 2, sm: 3, lg: 4 },
            }
          : {}),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
        <BackButton onClick={onBackClick} />
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h1" sx={{ fontSize: "1.125rem", fontWeight: 700, color: "text.primary", lineHeight: 1.2 }}>
            {isEditMode ? 'Edit Email Campaign' : 'Create Email Campaign'}
          </Typography>
          <Typography
            noWrap
            sx={{ color: "text.secondary", fontSize: 12.5, lineHeight: 1.3, display: { xs: "none", sm: "block" } }}
          >
            {isEditMode
              ? 'Update your email marketing campaign'
              : 'Set up a new email marketing campaign for your prospects and customers'}
          </Typography>
        </Box>
      </Box>

      {canSave && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          {onSendTest && (
            <Button
              onClick={onSendTest}
              variant="outlined"
              color="inherit"
              size="small"
              startIcon={<TestTube size={16} />}
            >
              Send Test
            </Button>
          )}
          <Button
            onClick={onSave}
            disabled={isSaving}
            variant="contained"
            color="success"
            size="small"
            startIcon={<Send size={16} />}
          >
            {isSaving ? "Saving..." : "Save campaign"}
          </Button>
        </Box>
      )}
    </Box>
  )
}
